import type { ReactNode } from 'react';
import React, { createContext, useCallback, useMemo } from 'react';

import type {
  AIProviderEnum,
  AnalysisIdOrLatest,
  BrandId,
} from '../api/generated/insights.schemas';
import type { BrandAnalysisMutations } from './types';

/**
 * Context for providing brand analysis mutation functions throughout the application.
 * Provides functions to update the active brand ID, analysis ID, and model.
 */

interface BrandAnalysisMutationsContextType {
  mutations: BrandAnalysisMutations;
}

const BrandAnalysisMutationsContext = createContext<
  BrandAnalysisMutationsContextType | undefined
>(undefined);

export { BrandAnalysisMutationsContext };

interface BrandAnalysisMutationsProviderProps {
  brandId: BrandId | undefined;
  setBrandId: React.Dispatch<React.SetStateAction<BrandId | undefined>>;
  analysisId: AnalysisIdOrLatest;
  setAnalysisId: React.Dispatch<React.SetStateAction<AnalysisIdOrLatest>>;
  model: AIProviderEnum | undefined;
  setModel: React.Dispatch<React.SetStateAction<AIProviderEnum | undefined>>;
  children: ReactNode;
}

export const BrandAnalysisMutationsProvider: React.FC<
  BrandAnalysisMutationsProviderProps
> = ({ children, setBrandId, setAnalysisId, setModel }) => {
  const handleSetBrandId = useCallback(
    (newBrandId: string | undefined) => {
      setBrandId(newBrandId);
      // When brand changes, reset analysis to latest
      setAnalysisId('latest');
    },
    [setBrandId, setAnalysisId],
  );

  const handleSetAnalysisId = useCallback(
    (newAnalysisId: AnalysisIdOrLatest) => {
      setAnalysisId(newAnalysisId);
    },
    [setAnalysisId],
  );

  const handleSetModel = useCallback(
    (newModel: AIProviderEnum | undefined) => {
      setModel(newModel);
    },
    [setModel],
  );

  const mutations = useMemo<BrandAnalysisMutations>(
    () => ({
      setBrandId: handleSetBrandId,
      setAnalysisId: handleSetAnalysisId,
      setModel: handleSetModel,
    }),
    [handleSetBrandId, handleSetAnalysisId, handleSetModel],
  );

  const value = useMemo(
    () => ({
      mutations,
    }),
    [mutations],
  );

  return (
    <BrandAnalysisMutationsContext.Provider value={value}>
      {children}
    </BrandAnalysisMutationsContext.Provider>
  );
};
